import React from 'react';
import { Skeleton, Card } from 'antd';

/**
 * Shared skeleton primitives — fully fluid, no min-width overflow.
 *
 * NOTE: Ant Design's `Skeleton.Input` has a default min-width of 160-240px
 * which causes horizontal overflow in narrow grid cells. We use `SkBar`
 * (a plain div with Tailwind animate-pulse) for text placeholders instead.
 */

/** A fluid skeleton bar — fills 100% of its container. Safe in any flex/grid cell. */
export const SkBar = ({ h = 'h-3', w = 'w-full', className = '', style = {} }) => (
  <div
    className={`${h} ${w} bg-gray-200 rounded animate-pulse ${className}`}
    style={style}
  />
);

export const SkHeader = ({ withAvatar = true, actions = 2 }) => (
  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
    <div className="flex items-center gap-3 min-w-0 flex-1">
      {withAvatar && <Skeleton.Avatar active size="large" shape="square" />}
      <div className="flex flex-col gap-2 flex-1 min-w-0">
        <SkBar h="h-6" w="w-full" className="max-w-[240px]" />
        <SkBar h="h-3" w="w-full" className="max-w-[180px]" />
      </div>
    </div>
    {actions > 0 && (
      <div className="flex flex-wrap gap-2 flex-shrink-0">
        {Array.from({ length: actions }).map((_, i) => (
          <SkBar key={i} h="h-9" w="w-24" className="rounded-md" />
        ))}
      </div>
    )}
  </div>
);

export const SkStatCards = ({ count = 4 }) => {
  const colClass =
    count === 5
      ? 'lg:grid-cols-5'
      : count === 3
      ? 'lg:grid-cols-3'
      : 'lg:grid-cols-4';
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${colClass} gap-3 sm:gap-4 mb-4 sm:mb-6`}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="shadow-sm" styles={{ body: { padding: 16 } }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <SkBar h="h-3" w="w-3/4" />
              <SkBar h="h-5" w="w-1/2" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export const SkFilterBar = ({ filters = 3, withButton = true }) => (
  <div className="flex flex-wrap gap-3 mb-4">
    <SkBar h="h-10" w="w-full sm:w-[320px]" className="rounded-md max-w-full" />
    {Array.from({ length: Math.max(0, filters - 1) }).map((_, i) => (
      <SkBar key={i} h="h-10" w="w-full sm:w-[180px]" className="rounded-md max-w-full" />
    ))}
    {withButton && <SkBar h="h-10" w="w-24" className="rounded-md flex-shrink-0" />}
  </div>
);

export const SkTable = ({ rows = 8, cols = 6, inCard = true }) => {
  const inner = (
    <>
      {/* Header row */}
      <div className="flex gap-3 sm:gap-4 pb-3 border-b border-gray-200 mb-3">
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className="flex-1 min-w-0">
            <SkBar h="h-3" w="w-full" />
          </div>
        ))}
      </div>
      {/* Data rows */}
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3 sm:gap-4 py-3 border-b border-gray-100 last:border-b-0">
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className="flex-1 min-w-0">
              <SkBar h="h-4" w="w-full" />
            </div>
          ))}
        </div>
      ))}
    </>
  );
  return inCard ? <Card className="shadow-sm">{inner}</Card> : inner;
};

export const SkTabsBar = ({ tabs = 4 }) => (
  <div className="flex gap-4 sm:gap-6 border-b border-gray-200 mb-4 overflow-x-auto">
    {Array.from({ length: tabs }).map((_, i) => (
      <div key={i} className="pb-2 flex-shrink-0">
        <SkBar h="h-4" w="w-20" />
      </div>
    ))}
  </div>
);

export const SkFormFields = ({ sections = 2, fields = 4 }) => (
  <>
    {Array.from({ length: sections }).map((_, s) => (
      <div key={s} className={s > 0 ? 'mt-6 pt-6 border-t border-gray-100' : ''}>
        <SkBar h="h-4" w="w-40" className="mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: fields }).map((_, f) => (
            <div key={f} className="flex flex-col gap-2">
              <SkBar h="h-3" w="w-24" />
              <SkBar h="h-10" w="w-full" className="rounded-md" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </>
);

export const SkChartCard = ({ title = true, heightClass = 'h-60 sm:h-64 md:h-72' }) => (
  <Card className="shadow-sm">
    {title && <SkBar h="h-4" w="w-full" className="max-w-[180px] mb-3" />}
    <div className={`w-full ${heightClass} bg-gray-100 rounded animate-pulse`} />
  </Card>
);

export const SkActivityList = ({ rows = 5 }) => (
  <Card className="shadow-sm">
    <SkBar h="h-4" w="w-full" className="max-w-[160px] mb-4" />
    {Array.from({ length: rows }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-2 sm:gap-3 py-2 sm:py-3 border-b border-gray-100 last:border-b-0"
      >
        <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
        <div className="flex-1 flex flex-col gap-1 min-w-0">
          <SkBar h="h-3" w="w-3/5" />
          <SkBar h="h-2" w="w-2/5" />
        </div>
        <div className="flex-shrink-0">
          <SkBar h="h-3" w="w-16" />
        </div>
      </div>
    ))}
  </Card>
);

export const SkAlertBanner = () => (
  <div className="mb-4 p-3 border border-blue-200 bg-blue-50 rounded-lg">
    <SkBar h="h-3" w="w-4/5" />
  </div>
);
